// User-facing controls settings — mouse-look feel. Persisted to
// localStorage and applied to CONFIG.controls. Input.onMouseMove reads the
// CONFIG fields each event so a slider drag during pointer-lock takes
// effect immediately on the next mouse movement.

import { CONFIG } from '../config';

export interface ControlsSettings {
  /** Mouse-look multiplier on yaw + pitch deltas. 1.0 is the default feel;
   *  the settings UI exposes 0.5..2.0. */
  mouseSensitivity: number;
  /** When true, mouse Y is inverted (pulling down looks up). */
  invertY: boolean;
}

export const DEFAULTS: ControlsSettings = {
  mouseSensitivity: 1.0,
  invertY: false
};

const SENSITIVITY_MIN = 0.5;
const SENSITIVITY_MAX = 2.0;
const STORAGE_KEY = 'games:controlsSettings';

export function loadControlsSettings(): ControlsSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      mouseSensitivity: clampSens(parsed.mouseSensitivity),
      invertY: typeof parsed.invertY === 'boolean' ? parsed.invertY : DEFAULTS.invertY
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveControlsSettings(s: ControlsSettings): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

export function applyControlsSettings(s: ControlsSettings): void {
  CONFIG.controls.mouseSensitivity = clampSens(s.mouseSensitivity);
  CONFIG.controls.invertY = s.invertY;
}

export const SENSITIVITY_RANGE = { min: SENSITIVITY_MIN, max: SENSITIVITY_MAX };

function clampSens(v: unknown): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return DEFAULTS.mouseSensitivity;
  return Math.max(SENSITIVITY_MIN, Math.min(SENSITIVITY_MAX, v));
}
